/**
 * [INPUT]: 路由 gameId、AdminApi 游戏详情与通用 Prompt CRUD 端口。
 * [OUTPUT]: 对外提供默认基础/压缩 Prompt 与任意自定义 Prompt 的目录、单条编辑、ID 复制和 token 上限配置。
 * [POS]: admin-web/pages 的 Prompt 控制面；前端按稳定 Prompt ID 调用，正文仅在受保护的管理接口读写。
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock3,
  Copy,
  Database,
  Plus,
  RefreshCcw,
  Save,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, ErrorState, Input, Kicker, LoadingBlock, Textarea } from '../components/ui';
import { useAsync } from '../hooks/use-async';
import { adminApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import type { GameAIPrompt, GameAIPromptEntryWriteInput } from '../types';

const MIN_OUTPUT_TOKENS = 256;
const MAX_OUTPUT_TOKENS = 16000;
const MIN_INPUT_TOKENS = 256;
const MAX_INPUT_TOKENS = 200000;

const emptyDraft: GameAIPromptEntryWriteInput = {
  name: '',
  content: '',
  maxInputTokens: 60000,
  maxOutputTokens: 1600,
};

export function GamePromptPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(async () => {
    const [game, prompts] = await Promise.all([adminApi.getGame(id), adminApi.listGameAIPrompts(id)]);
    return { game, prompts: prompts.items };
  }, [id]);
  const [items, setItems] = useState<GameAIPrompt[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState<GameAIPromptEntryWriteInput>(emptyDraft);
  const [baseline, setBaseline] = useState<GameAIPromptEntryWriteInput>(emptyDraft);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) return;
    setItems(data.prompts);
    const selected = data.prompts.find((item) => item.id === selectedId) ?? data.prompts[0];
    if (selected) openSavedPrompt(selected);
  // selectedId 只用于刷新后尽量保留当前位置；切换由显式交互处理。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selected = items.find((item) => item.id === selectedId) ?? null;
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [baseline, draft]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function openSavedPrompt(prompt: GameAIPrompt) {
    const next = toDraft(prompt);
    setSelectedId(prompt.id);
    setDraft(next);
    setBaseline(next);
    setCreating(false);
    setCopied(false);
    setNotice('');
    setSaveError('');
  }

  function selectPrompt(prompt: GameAIPrompt) {
    if (dirty && !window.confirm('当前修改尚未保存。要放弃修改并切换提示词吗？')) return;
    openSavedPrompt(prompt);
  }

  function startCreating() {
    if (dirty && !window.confirm('当前修改尚未保存。要放弃修改并新增提示词吗？')) return;
    setSelectedId('');
    setDraft({ name: '新提示词', content: '', maxInputTokens: 60000, maxOutputTokens: 1600 });
    setBaseline(emptyDraft);
    setCreating(true);
    setCopied(false);
    setNotice('');
    setSaveError('');
  }

  function update<K extends keyof GameAIPromptEntryWriteInput>(field: K, value: GameAIPromptEntryWriteInput[K]) {
    setDraft((current) => ({ ...current, [field]: value }));
    setNotice('');
    setSaveError('');
  }

  async function savePrompt() {
    const validation = validateDraft(draft);
    if (validation) {
      setSaveError(validation);
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = normalizeDraft(draft);
      const saved = creating
        ? await adminApi.createGameAIPrompt(id, payload)
        : await adminApi.updateGameAIPromptEntry(id, selectedId, payload);
      setItems((current) => creating ? [...current, saved] : current.map((item) => item.id === saved.id ? saved : item));
      openSavedPrompt(saved);
      setNotice(creating ? '提示词已创建，可以使用下方 ID 接入玩家前端。' : '提示词已保存，下一次使用该 ID 的请求会读取新正文。');
    } catch (cause) {
      setSaveError(cause instanceof Error ? cause.message : '保存提示词失败。');
    } finally {
      setSaving(false);
    }
  }

  async function copyPromptId() {
    if (!selected) return;
    await navigator.clipboard.writeText(selected.id);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  }

  if (loading && !data) return <PromptSkeleton />;
  if (error && !data) return <ErrorState message={error.message} onRetry={reload} />;
  if (!data?.game) return <ErrorState message="找不到这个游戏。" onRetry={() => navigate('/games')} />;

  const game = data.game;

  return <div className="space-y-6 pb-24">
    <button className="inline-flex min-h-11 items-center gap-2 text-xs font-medium text-slate-500 transition hover:text-moss focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-moss/30" onClick={() => navigate(`/games/${game.id}`)}><ArrowLeft className="h-3.5 w-3.5" />返回游戏详情</button>

    <header className="grid gap-5 border-b border-line pb-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
      <div>
        <Kicker>Prompt Registry / {game.slug}</Kicker>
        <div className="mt-2 flex flex-wrap items-center gap-3"><h1 className="text-2xl font-semibold tracking-[-0.035em] text-ink sm:text-3xl">{game.title} · 提示词</h1><span className="inline-flex items-center gap-1.5 rounded-full bg-mint px-2.5 py-1 text-[11px] font-semibold text-moss"><Database className="h-3 w-3" />{items.length} 条</span></div>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">基础与压缩是默认入口；新增 Prompt 会获得独立 ID，玩家前端按 ID 调用。</p>
      </div>
      <div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => void reload()} disabled={loading || dirty}><RefreshCcw className="h-3.5 w-3.5" />重新读取</Button><Button size="sm" onClick={startCreating}><Plus className="h-4 w-4" />新增提示词</Button></div>
    </header>

    {notice && <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs leading-5 text-emerald-800"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /><span>{notice}</span></div>}
    {saveError && <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs leading-5 text-rose-800" role="alert"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{saveError}</span></div>}

    <div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside>
        <div className="border-y border-line">
          {items.map((item) => <button key={item.id} type="button" onClick={() => selectPrompt(item)} className={`group flex w-full min-h-16 items-center justify-between gap-3 border-b border-line px-3 py-3 text-left transition last:border-b-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-moss/30 ${selectedId === item.id ? 'bg-mint/55 text-ink' : 'hover:bg-white text-slate-600'}`}><span className="min-w-0"><span className="block truncate text-sm font-semibold">{item.name}</span><span className="mt-1 block font-mono text-[10px] text-slate-400">输入 {item.maxInputTokens.toLocaleString()} · 输出 {item.maxOutputTokens.toLocaleString()}</span></span><PromptKind kind={item.kind} /></button>)}
          {creating && <div className="flex min-h-16 items-center justify-between gap-3 bg-mint/55 px-3 py-3"><span className="text-sm font-semibold text-ink">尚未保存的新提示词</span><PromptKind kind="custom" /></div>}
        </div>
      </aside>

      <main className="min-w-0">
        <div className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_180px_180px]">
          <Field label="名称"><Input value={draft.name} maxLength={80} onChange={(event) => update('name', event.target.value)} /></Field>
          <Field label="输入 token 上限" hint="256–200000"><Input type="number" min={MIN_INPUT_TOKENS} max={MAX_INPUT_TOKENS} step={256} value={draft.maxInputTokens} onChange={(event) => update('maxInputTokens', Number(event.target.value))} /></Field>
          <Field label="输出 token 上限" hint="256–16000"><Input type="number" min={MIN_OUTPUT_TOKENS} max={MAX_OUTPUT_TOKENS} step={128} value={draft.maxOutputTokens} onChange={(event) => update('maxOutputTokens', Number(event.target.value))} /></Field>
        </div>

        {selected && <div className="mt-5 border-y border-line py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Prompt ID</p><div className="mt-1 flex items-center gap-2"><code className="min-w-0 flex-1 truncate text-xs text-slate-600">{selected.id}</code><Button variant="outline" size="sm" onClick={() => void copyPromptId()}>{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? '已复制' : '复制 ID'}</Button></div></div>}

        <Field label="Prompt 正文" hint="仅服务端读取，玩家前端不会收到正文。" className="mt-6"><Textarea className="min-h-[38rem] font-mono text-[12px] leading-6" value={draft.content} spellCheck={false} onChange={(event) => update('content', event.target.value)} /><p className="mt-1.5 text-right text-[10px] tabular-nums text-slate-400">{Array.from(draft.content).length.toLocaleString()} 字符</p></Field>

        {selected && <p className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-400"><Clock3 className="h-3.5 w-3.5" />更新于 {formatDate(selected.updatedAt, true)}{selected.kind !== 'custom' && ' · 默认提示词不可删除'}</p>}
      </main>
    </div>

    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur-xl lg:left-[248px] lg:px-8"><div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4"><div className="min-w-0"><p className="text-xs font-semibold text-ink">{dirty ? '有未保存修改' : creating ? '正在新增提示词' : '当前提示词已同步'}</p><p className="mt-0.5 truncate text-[11px] text-slate-500">{selected ? `前端调用 ID：${selected.id}` : '保存后生成稳定 Prompt ID'}</p></div><Button onClick={() => void savePrompt()} disabled={saving || !dirty}><Save className="h-4 w-4" />{saving ? '保存中…' : creating ? '创建提示词' : '保存提示词'}</Button></div></div>
  </div>;
}

function PromptKind({ kind }: { kind: GameAIPrompt['kind'] }) {
  const label = kind === 'base' ? '基础' : kind === 'compression' ? '压缩' : '自定义';
  return <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold ${kind === 'base' ? 'bg-moss text-white' : kind === 'compression' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{label}</span>;
}

function Field({ label, hint, className = '', children }: { label: string; hint?: string; className?: string; children: ReactNode }) {
  return <label className={`block ${className}`}><span className="text-xs font-semibold text-ink">{label}</span>{hint && <span className="ml-2 text-[11px] text-slate-400">{hint}</span>}<div className="mt-2">{children}</div></label>;
}

function PromptSkeleton() {
  return <div className="space-y-6"><LoadingBlock className="h-5 w-28" /><LoadingBlock className="h-20 w-full" /><div className="grid gap-8 lg:grid-cols-[260px_minmax(0,1fr)]"><LoadingBlock className="h-64" /><LoadingBlock className="h-[44rem]" /></div></div>;
}

function toDraft(prompt: GameAIPrompt): GameAIPromptEntryWriteInput {
  return { name: prompt.name, content: prompt.content, maxInputTokens: prompt.maxInputTokens, maxOutputTokens: prompt.maxOutputTokens };
}

function normalizeDraft(draft: GameAIPromptEntryWriteInput): GameAIPromptEntryWriteInput {
  return { name: draft.name.trim(), content: draft.content.trim(), maxInputTokens: draft.maxInputTokens, maxOutputTokens: draft.maxOutputTokens };
}

function validateDraft(draft: GameAIPromptEntryWriteInput): string {
  if (!draft.name.trim()) return '请填写提示词名称。';
  if (!draft.content.trim()) return '请填写 Prompt 正文。';
  if (Array.from(draft.content).length > 120000) return 'Prompt 正文不能超过 120000 个字符。';
  if (!Number.isInteger(draft.maxInputTokens) || draft.maxInputTokens < MIN_INPUT_TOKENS || draft.maxInputTokens > MAX_INPUT_TOKENS) return '输入 token 上限必须是 256–200000 之间的整数。';
  if (!Number.isInteger(draft.maxOutputTokens) || draft.maxOutputTokens < MIN_OUTPUT_TOKENS || draft.maxOutputTokens > MAX_OUTPUT_TOKENS) return '输出 token 上限必须是 256–16000 之间的整数。';
  return '';
}
